import { Fragment, useMemo, useState } from 'react';
import {
  ArrowPathIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, LightBulbIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  useRecordVarianceOutcome, useStockVariances, useVarianceDetail, useVarianceHistory,
} from '../hooks/useStockVariances';

// "The count doesn't match the books."
//
// For every pair of consecutive counts on a van, this works out what the van
// should have held at the second count - the first count, plus what was
// collected, less what went on jobs or was written off faulty - and flags every
// line where the count disagrees. Each flag gets an outcome recorded against
// it, so nothing is looked at twice and nothing is forgotten.
//
// One table, one row per flagged van count, collapsed by default: the list is
// scanned van by van and a van is opened to deal with its lines. Stock Check
// answers "what does this van hold now"; this answers "where did it stop adding
// up, and has anyone dealt with it". Recording an outcome never changes the
// books.

const STATUS = {
  open: { label: 'Open', cls: 'bg-gray-100 text-gray-700' },
  chasing: { label: 'Needs chasing', cls: 'bg-amber-100 text-amber-800' },
  explained: { label: 'Explained', cls: 'bg-emerald-100 text-emerald-800' },
  written_off: { label: 'Written off', cls: 'bg-red-100 text-red-800' },
};

// Van colour, worked out on the server from what is still open. Full class
// strings on purpose - Tailwind only ships classes it can see written out.
const RATING = {
  red: {
    label: 'Investigate', order: 0,
    bar: 'border-l-red-500', row: 'bg-red-50/60 hover:bg-red-50', pill: 'bg-red-100 text-red-800', dot: 'bg-red-500',
  },
  orange: {
    label: 'Check', order: 1,
    bar: 'border-l-orange-500', row: 'bg-orange-50/60 hover:bg-orange-50', pill: 'bg-orange-100 text-orange-800', dot: 'bg-orange-500',
  },
  yellow: {
    label: 'Minor', order: 2,
    bar: 'border-l-yellow-400', row: 'bg-yellow-50/60 hover:bg-yellow-50', pill: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-400',
  },
  green: {
    label: 'Good', order: 3,
    bar: 'border-l-green-500', row: 'bg-green-50/40 hover:bg-green-50', pill: 'bg-green-100 text-green-800', dot: 'bg-green-500',
  },
};

const TABS = [
  ['to_action', 'To action'],
  ['chasing', 'Needs chasing'],
  ['explained', 'Explained'],
  ['written_off', 'Written off'],
  ['all', 'All'],
];

const PAGE_SIZES = [25, 50, 100];

const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 });
const ukDate = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return day ? `${day}/${m}/${y}` : String(d);
};
const ukDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

const sel = 'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none';

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.open;
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

function Difference({ value }) {
  if (value < 0) {
    return <span className="font-semibold tabular-nums text-red-700">{num(value)} <span className="text-xs font-normal">missing</span></span>;
  }
  return <span className="font-semibold tabular-nums text-sky-700">+{num(value)} <span className="text-xs font-normal">extra</span></span>;
}

// The outcome form, used for one line or every open line on a van count.
function OutcomeForm({
  pair, items, initial, onDone,
}) {
  const record = useRecordVarianceOutcome();
  const [status, setStatus] = useState(initial?.status && initial.status !== 'open' ? initial.status : 'explained');
  const [reason, setReason] = useState(initial?.reason || '');
  const needsReason = status !== 'chasing';
  const valid = !needsReason || reason.trim().length >= 3;

  const save = () => record.mutate({
    fromCountId: pair.from.id,
    toCountId: pair.to.id,
    stockItemIds: items.map((i) => i.stock_item_id),
    status,
    reason,
  }, { onSuccess: onDone });

  return (
    <div className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap gap-2">
        {['explained', 'written_off', 'chasing'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
              status === s ? 'border-brand bg-brand text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {STATUS[s].label}
          </button>
        ))}
      </div>
      <textarea
        id={`reason-${pair.key}-${items.length === 1 ? items[0].stock_item_id : 'all'}`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder={status === 'written_off'
          ? 'Why is this being written off? e.g. damaged in van, lost, confirmed not recoverable'
          : status === 'explained'
            ? 'What explains it? e.g. fitted on WO1451… but not logged, recount confirmed, handed back'
            : 'Optional: who is chasing it, and what for'}
        className={`${sel} w-full`}
      />
      {record.isError ? (
        <p className="text-xs text-red-600">{record.error?.response?.data?.error || 'Could not save that outcome.'}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!valid || record.isPending}
          className="rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
        >
          {record.isPending ? 'Saving…' : items.length > 1 ? `Record for all ${items.length} open lines` : 'Record outcome'}
        </button>
        <button type="button" onClick={onDone} className="text-sm text-gray-500 hover:text-gray-800">Cancel</button>
        {needsReason && !valid ? <span className="text-xs text-gray-400">A reason is required.</span> : null}
      </div>
    </div>
  );
}

function History({ pair, item }) {
  const { data, isLoading } = useVarianceHistory({ from: pair.from.id, to: pair.to.id, item: item.stock_item_id }, true);
  if (isLoading) return <p className="text-xs text-gray-400">Loading history…</p>;
  if (!data?.length) return null;
  return (
    <ul className="space-y-1 text-xs text-gray-500">
      {data.map((h) => (
        <li key={`${h.recorded_at}-${h.status}`}>
          <StatusPill status={h.status} />{' '}
          {h.reason ? `“${h.reason}” ` : ''}
          <span className="text-gray-400">
            — {h.recorded_by_name || 'someone'}, {ukDateTime(h.recorded_at)} (difference then {num(h.difference_qty)})
          </span>
        </li>
      ))}
    </ul>
  );
}

// A small titled list inside the breakdown. Says "None" rather than vanishing,
// so a reviewer can see a source was checked and came back empty.
function Section({
  title, total, sign, children, empty,
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-1.5">
        <span className="text-xs font-semibold text-gray-700">{title}</span>
        {total !== undefined ? (
          <span className="text-xs font-semibold tabular-nums text-gray-700">
            {sign}{num(Math.abs(total))}
          </span>
        ) : null}
      </div>
      {empty ? <p className="px-3 py-2 text-xs text-gray-400">None</p> : children}
    </div>
  );
}

const MiniTable = ({ head, rows }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-xs">
      <thead className="text-left text-[10px] uppercase tracking-wide text-gray-400">
        <tr>{head.map((h) => <th key={h} className="px-3 py-1 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-50 text-gray-700">{rows}</tbody>
    </table>
  </div>
);

// Everything behind one line: the working step by step, every record that
// makes up each figure, and leads that probably explain the difference.
function ItemBreakdown({ pair, item }) {
  const { data: d, isLoading, isError } = useVarianceDetail(
    { from: pair.from.id, to: pair.to.id, item: item.stock_item_id }, true,
  );
  if (isLoading) return <p className="text-xs text-gray-400">Loading the breakdown…</p>;
  if (isError || !d) return <p className="text-xs text-red-600">Could not load the breakdown for this line.</p>;

  const w = d.working;
  const L = d.leads;
  const leadCount = L.received_after_count.length + L.used_but_booked_to_another_van.length
    + L.completed_jobs_with_no_stock_logged.length + L.usage_logged_by_someone_else.length;

  const step = (label, value, cls = 'text-gray-900', sub = null) => (
    <div className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-center">
      <div className={`text-sm font-bold tabular-nums ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      {sub ? <div className="text-[10px] text-gray-400">{sub}</div> : null}
    </div>
  );
  const op = (s) => <span className="self-center text-gray-400">{s}</span>;

  return (
    <div className="space-y-3">
      {/* The working, left to right, so the arithmetic is visible. */}
      <div className="flex flex-wrap items-stretch gap-1.5">
        {step('Opening count', num(w.opening), 'text-gray-900', `#${d.from.id} · ${d.from.counted_by || '?'} · ${ukDateTime(d.from.at)}`)}
        {op('+')}
        {step('Collected', num(w.received), 'text-emerald-700')}
        {op('−')}
        {step('Used on jobs', num(w.used))}
        {op('−')}
        {step('Faulty', num(w.faulty), 'text-amber-700')}
        {op('±')}
        {step('Adjusted', num(w.adjusted))}
        {op('=')}
        {step('Expected', num(w.expected))}
        {op('vs')}
        {step('Counted', num(w.counted), 'text-gray-900', `#${d.to.id} · ${d.to.counted_by || '?'} · ${ukDateTime(d.to.at)}`)}
        {op('→')}
        {step('Difference', `${w.difference > 0 ? '+' : ''}${num(w.difference)}`, w.difference < 0 ? 'text-red-700' : 'text-sky-700')}
      </div>
      <p className="text-[11px] text-gray-500">
        {d.windows.movements}. {d.windows.jobs}.
      </p>

      <div className="grid gap-3 lg:grid-cols-2">
        <Section title="Collected and transferred" total={w.received} sign={w.received < 0 ? '−' : '+'} empty={!d.receipts.length}>
          <MiniTable
            head={['When', 'What', 'Reference', 'By', 'Qty']}
            rows={d.receipts.map((r) => (
              <tr key={`${r.at}-${r.ref}-${r.qty}`}>
                <td className="whitespace-nowrap px-3 py-1">{ukDateTime(r.at)}</td>
                <td className="px-3 py-1">{r.source}</td>
                <td className="px-3 py-1 font-mono">{r.ref || '—'}</td>
                <td className="px-3 py-1">{r.by || '—'}</td>
                <td className={`px-3 py-1 text-right font-semibold tabular-nums ${r.qty < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{r.qty > 0 ? '+' : ''}{num(r.qty)}</td>
              </tr>
            ))}
          />
        </Section>

        {d.faulty.length || d.adjustments.length ? null : (
          <p className="self-center text-[11px] text-gray-400">No faulty write-offs or adjustments for this item between the counts.</p>
        )}

        {/* Full width, with its own scroll: a busy week is 30+ jobs, and squeezed
            into half the panel the logged-by and quantity columns fall off. */}
        <div className="lg:col-span-2">
        <Section title={`Used on jobs (${d.jobs.length})`} total={w.used} sign="−" empty={!d.jobs.length}>
          <div className="max-h-80 overflow-y-auto">
          <MiniTable
            head={['Job date', 'WO', 'Address', 'Logged', 'Qty']}
            rows={d.jobs.map((j) => (
              <tr key={`${j.wo}-${j.logged_at}-${j.qty}`}>
                <td className="whitespace-nowrap px-3 py-1">{ukDate(j.job_date)}</td>
                <td className="whitespace-nowrap px-3 py-1 font-mono">{j.wo}</td>
                <td className="max-w-[16rem] truncate px-3 py-1" title={j.address}>{j.address}</td>
                <td className="whitespace-nowrap px-3 py-1">
                  {j.logged_by || '—'} · {ukDateTime(j.logged_at)}
                  {j.logged_after_count ? <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">after the count</span> : null}
                </td>
                <td className="px-3 py-1 text-right font-semibold tabular-nums">−{num(j.qty)}</td>
              </tr>
            ))}
          />
          </div>
        </Section>
        </div>

        {d.faulty.length || d.adjustments.length ? (
          <>
            <Section title="Faulty write-offs" total={w.faulty} sign="−" empty={!d.faulty.length}>
              <MiniTable
                head={['Job date', 'WO', 'Reason', 'Qty']}
                rows={d.faulty.map((f) => (
                  <tr key={`${f.wo}-${f.at}`}>
                    <td className="whitespace-nowrap px-3 py-1">{ukDate(f.job_date)}</td>
                    <td className="whitespace-nowrap px-3 py-1 font-mono">{f.wo}</td>
                    <td className="px-3 py-1">{[f.reason, f.disposition].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="px-3 py-1 text-right font-semibold tabular-nums">−{num(f.qty)}</td>
                  </tr>
                ))}
              />
            </Section>
            <Section title="Adjustments" total={w.adjusted} sign={w.adjusted < 0 ? '−' : '+'} empty={!d.adjustments.length}>
              <MiniTable
                head={['When', 'Reason', 'By', 'Qty']}
                rows={d.adjustments.map((a) => (
                  <tr key={`${a.at}-${a.qty}`}>
                    <td className="whitespace-nowrap px-3 py-1">{ukDateTime(a.at)}</td>
                    <td className="px-3 py-1">{a.reason || '—'}</td>
                    <td className="px-3 py-1">{a.by || '—'}</td>
                    <td className="px-3 py-1 text-right font-semibold tabular-nums">{a.qty > 0 ? '+' : ''}{num(a.qty)}</td>
                  </tr>
                ))}
              />
            </Section>
          </>
        ) : null}
      </div>

      {/* Records that did not count towards this line but probably explain it. */}
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-900">
          <LightBulbIcon className="h-4 w-4" />
          Leads {leadCount ? `(${leadCount})` : ''}
        </div>
        {!leadCount ? (
          <p className="text-xs text-amber-800">
            No record we hold explains this difference. Worth asking {d.to.counted_by || 'the engineer'}, or a recount.
          </p>
        ) : (
          <div className="space-y-3 text-xs text-amber-900">
            {L.received_after_count.length ? (
              <div>
                <p className="font-semibold">Received within a day after the count — not counted above</p>
                <p className="text-amber-800">If this was on the van when counted, it explains extra stock.</p>
                <ul className="mt-1 space-y-0.5">
                  {L.received_after_count.map((r) => (
                    <li key={`${r.at}-${r.ref}`}>{ukDateTime(r.at)} · {r.source} {r.ref ? `(${r.ref})` : ''} · {r.qty > 0 ? '+' : ''}{num(r.qty)}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {L.used_but_booked_to_another_van.length ? (
              <div>
                <p className="font-semibold">Used by this engineer but booked to a different van</p>
                <p className="text-amber-800">Stock taken from this van but logged against another reads as missing here.</p>
                <ul className="mt-1 space-y-0.5">
                  {L.used_but_booked_to_another_van.map((u) => (
                    <li key={`${u.wo}-${u.booked_to_van}`}>{ukDate(u.job_date)} · <span className="font-mono">{u.wo}</span> · {num(u.qty)} booked to <span className="font-mono">{u.booked_to_van}</span></li>
                  ))}
                </ul>
              </div>
            ) : null}
            {L.completed_jobs_with_no_stock_logged.length ? (
              <div>
                <p className="font-semibold">
                  {L.completed_jobs_with_no_stock_logged.length} completed {L.completed_jobs_with_no_stock_logged.length === 1 ? 'job' : 'jobs'} with no stock logged
                </p>
                <p className="text-amber-800">Anything fitted on these still counts as on the van, so it reads as missing.</p>
                <div className="mt-1 max-h-40 overflow-y-auto">
                  <ul className="space-y-0.5">
                    {L.completed_jobs_with_no_stock_logged.map((j) => (
                      <li key={j.wo_number}>
                        {ukDate(j.job_date)} · <span className="font-mono">{j.wo_number}</span> · {j.address} · {j.install_status}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
            {L.usage_logged_by_someone_else.length ? (
              <div>
                <p className="font-semibold">Usage on this van logged by someone other than the engineer who counted</p>
                <ul className="mt-1 space-y-0.5">
                  {L.usage_logged_by_someone_else.map((j) => (
                    <li key={`${j.wo}-${j.logged_at}`}>{ukDate(j.job_date)} · <span className="font-mono">{j.wo}</span> · {num(j.qty)} logged by {j.logged_by}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// The lines inside an opened van row.
function PairLines({ pair }) {
  const [editing, setEditing] = useState(null); // stock_item_id | 'all' | null
  const [details, setDetails] = useState(() => new Set());
  const toggleDetail = (id) => setDetails((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const items = useMemo(
    () => [...pair.items].sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference)),
    [pair.items],
  );
  const openItems = items.filter((i) => i.status === 'open' || i.status === 'chasing');

  return (
    <div className="border-l-4 border-brand bg-gray-50 px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          Count #{pair.from.id} · {ukDate(pair.from.date)} · week {pair.from.week}{pair.from.off_cycle ? ' · off-cycle' : ''}
          {' → '}
          #{pair.to.id} · {ukDate(pair.to.date)} · week {pair.to.week}{pair.to.off_cycle ? ' · off-cycle' : ''}
          {pair.from.counted_by !== pair.to.counted_by ? ` · counted by ${pair.from.counted_by}, then ${pair.to.counted_by}` : ''}
        </p>
        {openItems.length > 1 ? (
          <button
            type="button"
            onClick={() => setEditing(editing === 'all' ? null : 'all')}
            className="rounded-md border border-brand bg-white px-2.5 py-1 text-xs font-semibold text-brand hover:bg-brand hover:text-white"
          >
            Record for all {openItems.length} open lines
          </button>
        ) : null}
      </div>

      {pair.hints.length ? (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <LightBulbIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">{pair.hints.join(' · ')}</p>
        </div>
      ) : null}

      {editing === 'all' ? (
        <div className="mb-3">
          <OutcomeForm pair={pair} items={openItems} onDone={() => setEditing(null)} />
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-white text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Item</th>
              <th className="px-3 py-2 text-right font-semibold">Opening</th>
              <th className="px-3 py-2 text-right font-semibold">Collected</th>
              <th className="px-3 py-2 text-right font-semibold">Used</th>
              <th className="px-3 py-2 text-right font-semibold">Faulty</th>
              <th className="px-3 py-2 text-right font-semibold">Adjusted</th>
              <th className="px-3 py-2 text-right font-semibold">Expected</th>
              <th className="px-3 py-2 text-right font-semibold">Counted</th>
              <th className="px-3 py-2 text-right font-semibold">Difference</th>
              <th className="px-3 py-2 text-left font-semibold">Outcome</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((i) => (
              <Fragment key={i.stock_item_id}>
                <tr
                  onClick={() => toggleDetail(i.stock_item_id)}
                  className={`cursor-pointer ${details.has(i.stock_item_id) ? 'bg-purple-50/60' : i.difference < 0 ? 'bg-red-50/30 hover:bg-red-50/60' : 'hover:bg-gray-50'}`}
                  aria-expanded={details.has(i.stock_item_id)}
                  title="Click for the full breakdown"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 text-gray-800">
                      {details.has(i.stock_item_id)
                        ? <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        : <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                      {i.name}
                    </div>
                    <div className="font-mono text-[11px] text-gray-400">{i.sku}</div>
                    {i.hints.map((h) => (
                      <div key={h} className="mt-0.5 flex items-start gap-1 text-[11px] text-amber-700">
                        <LightBulbIcon className="mt-px h-3 w-3 shrink-0" />
                        {h}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{num(i.opening)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                    {i.received ? `${i.received > 0 ? '+' : ''}${num(i.received)}` : ''}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">{i.used ? `−${num(i.used)}` : ''}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{i.faulty ? `−${num(i.faulty)}` : ''}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {i.adjusted ? `${i.adjusted > 0 ? '+' : ''}${num(i.adjusted)}` : ''}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900">{num(i.expected)}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">{num(i.counted)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right"><Difference value={i.difference} /></td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={i.status} />
                      <button
                        type="button"
                        onClick={() => setEditing(editing === i.stock_item_id ? null : i.stock_item_id)}
                        className="text-xs font-medium text-brand hover:text-brand-dark"
                      >
                        {i.status === 'open' ? 'Record' : 'Change'}
                      </button>
                    </div>
                    {i.outcome ? (
                      <div className="mt-1 max-w-xs text-[11px] text-gray-500">
                        {i.outcome.reason ? `“${i.outcome.reason}” ` : ''}
                        <span className="text-gray-400">— {i.outcome.recorded_by}, {ukDateTime(i.outcome.recorded_at)}</span>
                        {i.outcome.changed_since ? (
                          <div className="mt-0.5 flex items-center gap-1 font-medium text-amber-700">
                            <ExclamationTriangleIcon className="h-3 w-3" />
                            Numbers changed since: was {num(i.outcome.recorded_difference)}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
                {details.has(i.stock_item_id) ? (
                  <tr>
                    <td colSpan={10} className="border-l-4 border-purple-300 bg-purple-50/30 px-3 py-3">
                      <ItemBreakdown pair={pair} item={i} />
                    </td>
                  </tr>
                ) : null}
                {editing === i.stock_item_id ? (
                  <tr>
                    <td colSpan={10} className="bg-gray-50 px-3 py-3">
                      <OutcomeForm pair={pair} items={[i]} initial={{ status: i.status, reason: i.outcome?.reason }} onDone={() => setEditing(null)} />
                      {i.outcome ? <div className="mt-3"><History pair={pair} item={i} /></div> : null}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Per van-count figures the summary row shows and sorts on.
function summarise(pair) {
  const missing = pair.items.filter((i) => i.difference < 0).reduce((n, i) => n - i.difference, 0);
  const extra = pair.items.filter((i) => i.difference > 0).reduce((n, i) => n + i.difference, 0);
  const open = pair.items.filter((i) => i.status === 'open' || i.status === 'chasing').length;
  const hinted = pair.items.filter((i) => i.hints.length).length;
  return {
    missing, extra, open, hinted, size: missing + extra,
  };
}

const SORTS = {
  rating: (a, b) => (RATING[a.p.rating]?.order ?? 9) - (RATING[b.p.rating]?.order ?? 9)
    || (b.p.open_missing || 0) - (a.p.open_missing || 0) || b.s.size - a.s.size,
  size: (a, b) => b.s.size - a.s.size,
  missing: (a, b) => b.s.missing - a.s.missing,
  extra: (a, b) => b.s.extra - a.s.extra,
  lines: (a, b) => b.p.items.length - a.p.items.length,
  van: (a, b) => a.p.van.localeCompare(b.p.van),
  engineer: (a, b) => String(a.p.to.counted_by || '').localeCompare(String(b.p.to.counted_by || '')),
  date: (a, b) => (a.p.to.date < b.p.to.date ? 1 : a.p.to.date > b.p.to.date ? -1 : 0),
};

function StockVariances() {
  const [status, setStatus] = useState('to_action');
  const [week, setWeek] = useState('');
  const [isp, setIsp] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('rating');
  const [rating, setRating] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const filters = useMemo(() => ({
    status, week: week || undefined, isp: isp || undefined, q: q.trim() || undefined,
  }), [status, week, isp, q]);
  const {
    data, isLoading, isError, isFetching, refetch,
  } = useStockVariances(filters);

  const t = data?.totals || {};
  const ratingCounts = useMemo(() => {
    const c = {
      red: 0, orange: 0, yellow: 0, green: 0,
    };
    (data?.pairs || []).forEach((p) => { if (c[p.rating] !== undefined) c[p.rating] += 1; });
    return c;
  }, [data]);
  const rows = useMemo(
    () => (data?.pairs || [])
      .filter((p) => !rating || p.rating === rating)
      .map((p) => ({ p, s: summarise(p) }))
      .sort(SORTS[sort]),
    [data, sort, rating],
  );
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pages - 1);
  const shown = rows.slice(current * pageSize, (current + 1) * pageSize);

  const tabCount = {
    to_action: (t.open || 0) + (t.chasing || 0),
    chasing: t.chasing,
    explained: t.explained,
    written_off: t.written_off,
    all: t.flags,
  };
  const resetPage = (fn) => (v) => { fn(v); setPage(0); };
  const toggle = (key) => setOpenKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const expandable = shown.filter(({ p }) => p.items.length > 0);
  const allShownOpen = expandable.length > 0 && expandable.every(({ p }) => openKeys.has(p.key));

  const Head = ({ k, children, className = '' }) => (
    <th className={`px-3 py-2 font-semibold ${className}`}>
      <button
        type="button"
        onClick={() => setSort(k)}
        className={`uppercase tracking-wide ${sort === k ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}
      >
        {children}{sort === k ? ' ↓' : ''}
      </button>
    </th>
  );

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Variances</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Where a van&apos;s count didn&apos;t match its books. For every pair of consecutive counts, this
            works out what the van should have held — the first count, plus what was collected, less what
            went on jobs or was written off faulty — and flags every line the second count disagrees with.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {data ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {[
            ['Open', t.open, 'text-gray-900'],
            ['Needs chasing', t.chasing, 'text-amber-700'],
            ['Explained', t.explained, 'text-emerald-700'],
            ['Written off', t.written_off, 'text-red-700'],
            ['Units missing', t.units_missing, 'text-red-700'],
            ['Units extra', t.units_extra, 'text-sky-700'],
          ].map(([label, value, cls]) => (
            <div key={label} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
              <div className={`text-xl font-bold tabular-nums ${cls}`}>{num(value)}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex flex-wrap rounded-md border border-gray-300 bg-white p-0.5">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setStatus(k); setPage(0); }}
              className={`rounded px-3 py-1.5 text-sm font-medium ${
                status === k ? 'bg-brand text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
              {data ? <span className={`ml-1.5 text-xs ${status === k ? 'text-white/80' : 'text-gray-400'}`}>{num(tabCount[k])}</span> : null}
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            id="variance-search"
            value={q}
            onChange={(e) => resetPage(setQ)(e.target.value)}
            placeholder="Engineer or registration…"
            className={`${sel} w-56 pl-8`}
          />
        </div>
        <select id="variance-week" value={week} onChange={(e) => resetPage(setWeek)(e.target.value)} className={sel}>
          <option value="">All weeks</option>
          {(data?.weeks || []).map((w) => <option key={w} value={w}>Counted in week {w}</option>)}
        </select>
        <select id="variance-isp" value={isp} onChange={(e) => resetPage(setIsp)(e.target.value)} className={sel}>
          <option value="">Both regions</option>
          <option value="1">Region N</option>
          <option value="2">Region B</option>
        </select>
      </div>

      {isError ? <p className="mt-6 text-sm text-red-600">Could not load stock variances.</p> : null}
      {isLoading ? <p className="mt-6 text-sm text-gray-400">Working out every van…</p> : null}

      {data ? (
        <>
          {t.changed_since_recorded ? (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800">
                <strong>{t.changed_since_recorded}</strong> recorded {t.changed_since_recorded === 1 ? 'outcome has' : 'outcomes have'} had
                {' '}the numbers change since — usually stock logged late on a job. Worth a second look.
              </p>
            </div>
          ) : null}

          {/* Colour filter: the quickest way to the vans that matter. */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => { setRating(''); setPage(0); }}
              className={`rounded-full border px-3 py-1 text-sm font-medium ${
                rating === '' ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              All vans <span className="ml-1 opacity-70">{num((data.pairs || []).length)}</span>
            </button>
            {['red', 'orange', 'yellow', 'green'].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => { setRating(rating === r ? '' : r); setPage(0); }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium ${
                  rating === r ? `border-transparent ${RATING[r].pill} ring-2 ring-offset-1 ring-gray-400` : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${RATING[r].dot}`} />
                {RATING[r].label}
                <span className="opacity-70">{num(ratingCounts[r])}</span>
              </button>
            ))}
            {data.thresholds ? (
              <span className="text-xs text-gray-400">
                On open differences: red {data.thresholds.redMissing}+ units or {data.thresholds.redKit}+ routers/ONTs/mesh missing ·
                orange {data.thresholds.orangeMissing}–{data.thresholds.redMissing - 1} missing ·
                yellow 1–{data.thresholds.orangeMissing - 1} missing or extra only · green balances or all dealt with
              </span>
            ) : null}
          </div>

          <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2">
              <span className="text-xs text-gray-500">
                {num(rows.length)} van {rows.length === 1 ? 'count' : 'counts'} · worst first · click a row to open it
              </span>
              <button
                type="button"
                onClick={() => setOpenKeys(allShownOpen ? new Set() : new Set(expandable.map(({ p }) => p.key)))}
                className="text-xs font-medium text-brand hover:text-brand-dark"
              >
                {allShownOpen ? 'Collapse all' : 'Expand all on this page'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs">
                  <tr>
                    <th className="w-8 px-2 py-2" />
                    <Head k="rating">Rating</Head>
                    <Head k="van">Van</Head>
                    <Head k="engineer">Engineer</Head>
                    <Head k="date">Counts</Head>
                    <Head k="lines" className="text-right">Lines</Head>
                    <Head k="missing" className="text-right">Missing</Head>
                    <Head k="extra" className="text-right">Extra</Head>
                    <th className="px-3 py-2 font-semibold uppercase tracking-wide text-gray-500" title="Flagged lines on this van with no outcome recorded yet">Left to deal with</th>
                    <th className="px-3 py-2 font-semibold uppercase tracking-wide text-gray-500">Likely cause</th>
                  </tr>
                </thead>
                {shown.map(({ p, s }) => {
                  const hasLines = p.items.length > 0;
                  const isOpen = hasLines && openKeys.has(p.key);
                  const handover = p.from.counted_by !== p.to.counted_by;
                  const r = RATING[p.rating] || RATING.yellow;
                  return (
                    <tbody key={p.key} className="border-t border-gray-100">
                      <tr
                        onClick={() => hasLines && toggle(p.key)}
                        className={`border-l-4 ${r.bar} ${hasLines ? 'cursor-pointer' : ''} ${r.row}`}
                        aria-expanded={hasLines ? isOpen : undefined}
                      >
                        <td className="px-2 py-2.5 text-gray-400">
                          {!hasLines ? null : isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${r.pill}`}>
                            <span className={`inline-block h-2 w-2 rounded-full ${r.dot}`} />
                            {r.label}
                          </span>
                          <div className="mt-0.5 max-w-[14rem] text-[11px] leading-snug text-gray-500">{p.rating_reason}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="font-mono font-bold text-gray-900">{p.van}</span>
                          {p.region ? <span className="ml-2 text-xs text-gray-400">{p.region}</span> : null}
                        </td>
                        <td className="px-3 py-2.5 text-gray-700">
                          {p.to.counted_by}
                          {handover ? <div className="text-[11px] text-gray-400">was {p.from.counted_by}</div> : null}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                          {ukDate(p.from.date)} → {ukDate(p.to.date)}
                          <div className="text-[11px] text-gray-400">
                            week {p.from.week} → {p.to.week}{p.to.off_cycle ? ' · off-cycle' : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{p.items.length}</td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-red-700">{s.missing ? `−${num(s.missing)}` : ''}</td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-sky-700">{s.extra ? `+${num(s.extra)}` : ''}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                          {!hasLines ? <span className="text-green-700">Nothing to check</span>
                            : s.open ? (
                              <span className="text-gray-700">
                                {s.open} of {p.items.length} {p.items.length === 1 ? 'line' : 'lines'}
                              </span>
                            ) : <span className="text-green-700">All dealt with</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs">
                          <div className="flex flex-wrap gap-1">
                            {p.hints.map((h) => (
                              <span key={h} className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800" title={h}>
                                {h.startsWith('Van changed hands') ? 'Van changed hands' : 'Recount looks incomplete'}
                              </span>
                            ))}
                            {s.hinted ? (
                              <span className="inline-flex items-center gap-0.5 text-amber-700">
                                <LightBulbIcon className="h-3 w-3" />
                                {s.hinted} {s.hinted === 1 ? 'lead' : 'leads'}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <PairLines pair={p} />
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  );
                })}
                {!rows.length ? (
                  <tbody>
                    <tr>
                      <td colSpan={10} className="px-4 py-10 text-center text-sm text-gray-400">
                        {status === 'to_action' ? 'Nothing to action — every difference has an outcome.' : 'Nothing matches.'}
                      </td>
                    </tr>
                  </tbody>
                ) : null}
              </table>
            </div>

            {rows.length ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {current * pageSize + 1}–{Math.min((current + 1) * pageSize, rows.length)} of {rows.length}
                  </span>
                  <select
                    id="variance-page-size"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                    className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                  >
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} a page</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)} className="rounded-md border border-gray-300 px-2.5 py-1 text-xs disabled:opacity-40">Previous</button>
                  <span className="text-xs text-gray-500">{current + 1} / {pages}</span>
                  <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} className="rounded-md border border-gray-300 px-2.5 py-1 text-xs disabled:opacity-40">Next</button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
            <p className="font-semibold text-gray-700">How the expected figure is worked out</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>Counts are paired <strong>per van, per region</strong>, one count to the next — so regional half-counts and recounts each reconcile against the right earlier count.</li>
              <li>Stock used on jobs and faulty write-offs are placed by <strong>job date</strong>, not when they were logged — a job done before a count and logged after it was already off the van.</li>
              <li>Collections, transfers and adjustments are placed by <strong>when they happened</strong>, between the two counts.</li>
              <li>Only counts from {ukDate(data.live_from)} onwards, when job-card stock logging went live. Only items on <strong>both</strong> counts — a missing line is an unknown, not a zero.</li>
              <li>Pack consumables aren&apos;t on the van count, so they don&apos;t appear. The <LightBulbIcon className="inline h-3 w-3 text-amber-600" /> leads are likely causes, not conclusions. Recording an outcome never changes van stock.</li>
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default StockVariances;
